import { Fragment, useState, useRef, useEffect } from "react";
import { trpc } from "@/lib/trpc";
import UpdateBanner from "@/components/UpdateBanner";
import AppHeader from "@/components/AppHeader";
import { toast } from "@/lib/toast";
import { useAuth } from "@/hooks/useAuth";
import { useApplyDefaultLocation } from "@/hooks/useDefaultLocation";
import { Pencil, Trash2, Check, X, Tag, Search, Plus, FileText, ArrowRight, Link2, Upload, Download, FileSpreadsheet, GripVertical, Users, MapPin, User, ImagePlus } from "lucide-react";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  horizontalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { TagCsvImportDialog } from "@/components/TagCsvImportDialog";
import { ImageCropDialog } from "@/components/ImageCropDialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

type LibraryKind = "activity" | "topic" | "providers";

// Both routers expose an identical procedure shape — using one as the common
// type lets the same component drive either library.
type TagsApi = typeof trpc.tags;

const LIBRARY_LABELS: Record<"activity" | "topic", { tag: string; library: string }> = {
  activity: { tag: "Activity Tag", library: "Activity Tag Library" },
  topic: { tag: "Topic Tag", library: "Topic Tag Library" },
};

const MINNESOTA_PURPLE = "#6A5ACD";
const IOWA_BLUE = "#5B7C99";

export default function TagLibrary() {
  // Redirect to login if not authenticated (e.g. after logout)
  useAuth({ redirectOnUnauthenticated: true });

  const [library, setLibrary] = useState<LibraryKind>("activity");

  return (
    <div className="min-h-screen bg-background">
      <UpdateBanner />
      <AppHeader />

      <main className="container py-8">
        <div className="max-w-2xl mx-auto space-y-6">

          {/* ── Library selector ─────────────────────────────────────────── */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center gap-2">
                <Tag className="w-5 h-5 text-primary" />
                <CardTitle className="text-base">Tag Library</CardTitle>
              </div>
              <CardDescription>
                Choose which library to work in — Activity Tags describe what a patient is doing in a clip; Topic Tags group footage by subject matter; Providers are the clinicians associated with each Location.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-3">
                {(["activity", "topic", "providers"] as const).map((kind) => {
                  const isSelected = library === kind;
                  const label =
                    kind === "activity" ? "Activity Tags" : kind === "topic" ? "Topic Tags" : "Providers";
                  // Activity Tags uses the app's theme primary (green); Topic Tags
                  // keeps Minnesota purple; Providers uses Iowa blue.
                  const selectedClass =
                    kind === "activity"
                      ? "border-transparent bg-primary text-primary-foreground ring-2 ring-primary ring-offset-2 ring-offset-background shadow-sm"
                      : "border-transparent text-white shadow-sm";
                  const accent = kind === "topic" ? MINNESOTA_PURPLE : kind === "providers" ? IOWA_BLUE : null;
                  const selectedInlineStyle =
                    accent != null
                      ? {
                          backgroundColor: accent,
                          boxShadow: `0 0 0 2px var(--background), 0 0 0 4px ${accent}`,
                        }
                      : undefined;
                  return (
                    <button
                      key={kind}
                      type="button"
                      onClick={() => setLibrary(kind)}
                      className={cn(
                        "flex items-center justify-center gap-2 px-4 py-3 rounded-lg border text-sm font-semibold transition-all",
                        "focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
                        isSelected
                          ? selectedClass
                          : "border-border bg-background hover:bg-muted/60 hover:border-foreground/20 text-foreground",
                      )}
                      style={isSelected ? selectedInlineStyle : undefined}
                    >
                      {isSelected && <Check className="w-4 h-4" />}
                      {label}
                    </button>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* ── Selected library ─────────────────────────────────────────── */}
          {library === "providers" ? (
            <ProvidersSection />
          ) : (
            <LibrarySection
              key={library}
              library={library}
              api={(library === "activity" ? trpc.tags : (trpc.topicTags as unknown as TagsApi))}
            />
          )}

        </div>
      </main>
    </div>
  );
}

// ─── Reusable chip editor ─────────────────────────────────────────────────────
// Renders an autocomplete-aware input that stages typed names as chips. Used
// by the alias add-form and the alias inline-edit form. Owns its own input /
// dropdown / highlight state so multiple instances can coexist independently.

interface TagChipEditorProps {
  targets: string[];
  onChange: (targets: string[]) => void;
  api: TagsApi;
  disabled?: boolean;
  placeholder?: string;
  /** Invoked when the user presses Enter on an empty input with at least one
   *  chip present. Useful for triggering Save from the keyboard. */
  onEnterEmpty?: () => void;
  /** Compact mode trims paddings; used inside narrow row editors. */
  compact?: boolean;
}

function SortableTargetChip({
  id,
  label,
  onRemove,
  disabled,
}: {
  id: string;
  label: string;
  onRemove: () => void;
  disabled?: boolean;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 50 : undefined,
  };
  return (
    <span
      ref={setNodeRef}
      style={style}
      className={cn(
        "inline-flex items-center gap-1 pl-1 pr-1 py-0.5 rounded-md bg-muted border border-border text-xs select-none",
        isDragging && "ring-2 ring-primary/40 shadow-md",
      )}
    >
      {!disabled && (
        <span
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing text-muted-foreground/60 hover:text-muted-foreground touch-none px-0.5"
          title="Drag to reorder"
          onClick={(e) => e.stopPropagation()}
        >
          <GripVertical className="w-3 h-3" />
        </span>
      )}
      <span className="font-medium">{label}</span>
      <button
        type="button"
        onClick={onRemove}
        disabled={disabled}
        className="text-muted-foreground hover:text-destructive p-0.5 rounded"
        title="Remove target"
      >
        <X className="w-3 h-3" />
      </button>
    </span>
  );
}

function TagChipEditor({
  targets,
  onChange,
  api,
  disabled,
  placeholder,
  onEnterEmpty,
  compact,
}: TagChipEditorProps) {
  const [inputValue, setInputValue] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // dnd-kit sensors — small activation distance so single-click ×-remove still
  // fires reliably and a deliberate drag is needed to initiate reordering.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
  );
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = targets.indexOf(active.id as string);
    const newIndex = targets.indexOf(over.id as string);
    if (oldIndex !== -1 && newIndex !== -1) {
      onChange(arrayMove(targets, oldIndex, newIndex));
    }
  };

  const { data: suggestions = [] } = api.search.useQuery(
    { query: inputValue },
    { enabled: inputValue.trim().length > 0 },
  );

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        dropdownRef.current && !dropdownRef.current.contains(e.target as Node) &&
        inputRef.current && !inputRef.current.contains(e.target as Node)
      ) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const addChip = (name: string) => {
    const trimmed = name.trim();
    if (!trimmed) return;
    if (targets.some((c) => c.toLowerCase() === trimmed.toLowerCase())) {
      toast.error(`"${trimmed}" is already in the targets`);
      return;
    }
    onChange([...targets, trimmed]);
    setInputValue("");
    setShowDropdown(false);
    setHighlight(-1);
    inputRef.current?.focus();
  };

  const removeChip = (idx: number) => onChange(targets.filter((_, i) => i !== idx));

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, suggestions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, -1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (highlight >= 0 && suggestions[highlight]) {
        addChip(suggestions[highlight].name);
      } else if (inputValue.trim()) {
        addChip(inputValue);
      } else if (targets.length > 0 && onEnterEmpty) {
        onEnterEmpty();
      }
    } else if (e.key === "Backspace" && !inputValue && targets.length > 0) {
      e.preventDefault();
      removeChip(targets.length - 1);
    } else if (e.key === "Escape") {
      setShowDropdown(false);
    }
  };

  const resolvedPlaceholder =
    placeholder ?? (targets.length === 0 ? "Type a tag and press Enter…" : "Add another tag…");

  return (
    <div className={cn(compact ? "space-y-1" : "space-y-2")}>
      <div className="relative">
        <Input
          ref={inputRef}
          value={inputValue}
          onChange={(e) => {
            setInputValue(e.target.value);
            setShowDropdown(true);
            setHighlight(-1);
          }}
          onFocus={() => inputValue.trim() && setShowDropdown(true)}
          onKeyDown={onKeyDown}
          placeholder={resolvedPlaceholder}
          className={cn("text-sm", compact ? "h-8" : "h-9")}
          disabled={disabled}
          autoComplete="off"
        />
        {showDropdown && suggestions.length > 0 && (
          <div
            ref={dropdownRef}
            className="absolute z-50 top-full left-0 right-0 mt-1 bg-popover text-popover-foreground border border-border rounded-lg shadow-md overflow-hidden"
          >
            {suggestions.map((tag, idx) => (
              <button
                key={tag.id}
                type="button"
                onMouseDown={(e) => { e.preventDefault(); addChip(tag.name); }}
                className={cn(
                  "w-full text-left px-3 py-2 text-xs flex items-center justify-between gap-2 hover:bg-muted transition-colors",
                  idx === highlight && "bg-muted",
                )}
              >
                <span className="font-medium text-foreground">{tag.name}</span>
                <span className="text-muted-foreground text-[10px] shrink-0">{tag.usageCount}x</span>
              </button>
            ))}
          </div>
        )}
      </div>
      {targets.length > 0 && (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={targets} strategy={horizontalListSortingStrategy}>
            <div className="flex flex-wrap items-center gap-1.5">
              {targets.map((chip, idx) => (
                <SortableTargetChip
                  key={chip}
                  id={chip}
                  label={chip}
                  onRemove={() => removeChip(idx)}
                  disabled={disabled}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}
    </div>
  );
}

// ─── Reusable library section (library list + aliases) ────────────────────────

interface LibrarySectionProps {
  library: "activity" | "topic";
  api: TagsApi;
}

function LibrarySection({ library, api }: LibrarySectionProps) {
  const labels = LIBRARY_LABELS[library];

  const [search, setSearch] = useState("");
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingValue, setEditingValue] = useState("");
  const [newTagValue, setNewTagValue] = useState("");
  const [newTagDescription, setNewTagDescription] = useState("");

  // Description editing state — keyed by tag id
  const [editingDescId, setEditingDescId] = useState<number | null>(null);
  const [editingDescValue, setEditingDescValue] = useState("");
  const descTextareaRef = useRef<HTMLTextAreaElement>(null);

  // Alias form state
  const [csvImportOpen, setCsvImportOpen] = useState(false);

  // Add-alias form state (consumed by the alias card at the bottom of the section)
  const [aliasInput, setAliasInput] = useState("");
  const [addTargets, setAddTargets] = useState<string[]>([]);
  const [addDescription, setAddDescription] = useState("");

  // Inline-edit state — only one alias row is in edit mode at a time.
  const [editingAliasId, setEditingAliasId] = useState<number | null>(null);
  const [editingAliasValue, setEditingAliasValue] = useState("");
  const [editingTargets, setEditingTargets] = useState<string[]>([]);
  const [editingAliasDescription, setEditingAliasDescription] = useState("");

  const utils = trpc.useUtils();
  const utilsApi = (library === "activity" ? utils.tags : (utils.topicTags as unknown as typeof utils.tags));

  const { data: tags = [], isLoading, isError, error, refetch, isFetching } = api.list.useQuery(undefined, {
    retry: 2,
    retryDelay: 1000,
  });

  const { data: aliases = [], isLoading: aliasesLoading } = api.listAliases.useQuery();

  const renameMutation = api.rename.useMutation({
    onSuccess: () => {
      utilsApi.list.invalidate();
      setEditingId(null);
      setEditingValue("");
      toast.success("Tag renamed");
    },
    onError: (err) => toast.error(err.message ?? "Failed to rename tag"),
  });

  const createMutation = api.create.useMutation({
    onSuccess: () => {
      utilsApi.list.invalidate();
      setNewTagValue("");
      setNewTagDescription("");
      toast.success("Tag added");
    },
    onError: (err) => toast.error(err.message ?? "Failed to add tag"),
  });

  const descMutation = api.updateDescription.useMutation({
    onSuccess: () => {
      utilsApi.list.invalidate();
      setEditingDescId(null);
      setEditingDescValue("");
      toast.success("Description saved");
    },
    onError: (err) => toast.error(err.message ?? "Failed to save description"),
  });

  const deleteMutation = api.delete.useMutation({
    onMutate: async ({ id }) => {
      await utilsApi.list.cancel();
      const prev = utilsApi.list.getData();
      utilsApi.list.setData(undefined, (old) => old?.filter((t) => t.id !== id) ?? []);
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) utilsApi.list.setData(undefined, ctx.prev);
      toast.error("Failed to delete tag");
    },
    onSettled: () => utilsApi.list.invalidate(),
  });

  const createAliasMutation = api.createAlias.useMutation({
    onSuccess: () => {
      utilsApi.listAliases.invalidate();
      setAliasInput("");
      setAddTargets([]);
      setAddDescription("");
      toast.success("Alias created");
    },
    onError: (err) => toast.error(err.message ?? "Failed to create alias"),
  });

  const updateAliasMutation = api.updateAlias.useMutation({
    onSuccess: () => {
      utilsApi.listAliases.invalidate();
      setEditingAliasId(null);
      setEditingAliasValue("");
      setEditingTargets([]);
      setEditingAliasDescription("");
      toast.success("Alias updated");
    },
    onError: (err) => toast.error(err.message ?? "Failed to update alias"),
  });

  const deleteAliasMutation = api.deleteAlias.useMutation({
    onMutate: async ({ id }) => {
      await utilsApi.listAliases.cancel();
      const prev = utilsApi.listAliases.getData();
      utilsApi.listAliases.setData(undefined, (old) => old?.filter((a) => a.id !== id) ?? []);
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) utilsApi.listAliases.setData(undefined, ctx.prev);
      toast.error("Failed to delete alias");
    },
    onSettled: () => utilsApi.listAliases.invalidate(),
  });

  const handleAddTag = () => {
    const trimmed = newTagValue.trim();
    if (!trimmed) return;
    const description = newTagDescription.trim();
    createMutation.mutate({ name: trimmed, description: description || undefined });
  };

  // Export the current library as a CSV with columns: Tag, Description, Aliases.
  // Usage counts are intentionally omitted — this is a content snapshot, not analytics.
  const handleExportCsv = () => {
    if (tags.length === 0) {
      toast.error("Library is empty — nothing to export");
      return;
    }
    // Group aliases by canonicalName for O(1) lookup per tag.
    const aliasesByCanonical = new Map<string, string[]>();
    for (const a of aliases) {
      const key = a.canonicalName.toLowerCase();
      const list = aliasesByCanonical.get(key) ?? [];
      list.push(a.alias);
      aliasesByCanonical.set(key, list);
    }
    // RFC 4180: wrap fields containing comma, quote, CR, or LF in quotes;
    // escape embedded quotes by doubling them.
    const escape = (value: string): string => {
      if (/[",\r\n]/.test(value)) {
        return `"${value.replace(/"/g, '""')}"`;
      }
      return value;
    };
    const header = ["Tag", "Description", "Aliases"];
    const rows = tags.map((t) => {
      const tagAliases = aliasesByCanonical.get(t.name.toLowerCase()) ?? [];
      return [t.name, t.description ?? "", tagAliases.join("; ")].map(escape).join(",");
    });
    // BOM (U+FEFF) prefix so Excel opens UTF-8 without mojibake.
    const csv = "﻿" + [header.map(escape).join(","), ...rows].join("\r\n") + "\r\n";
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const today = new Date().toISOString().slice(0, 10);
    const slug = library === "activity" ? "activity-tags" : "topic-tags";
    const link = document.createElement("a");
    link.href = url;
    link.download = `${slug}-${today}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success(`Exported ${tags.length} ${tags.length === 1 ? "tag" : "tags"}`);
  };

  const handleAddAlias = () => {
    const alias = aliasInput.trim();
    if (!alias) { toast.error("Alias name is required"); return; }
    if (addTargets.length === 0) { toast.error("Add at least one target tag"); return; }
    createAliasMutation.mutate({
      alias,
      targets: addTargets,
      description: addDescription.trim() || undefined,
    });
  };

  const startEditAlias = (alias: {
    id: number;
    alias: string;
    targets?: string[] | null;
    canonicalName: string;
    description?: string | null;
  }) => {
    setEditingAliasId(alias.id);
    setEditingAliasValue(alias.alias);
    setEditingTargets(
      alias.targets && alias.targets.length > 0 ? [...alias.targets] : [alias.canonicalName],
    );
    setEditingAliasDescription(alias.description ?? "");
  };

  const cancelEditAlias = () => {
    setEditingAliasId(null);
    setEditingAliasValue("");
    setEditingTargets([]);
    setEditingAliasDescription("");
  };

  const commitEditAlias = () => {
    if (editingAliasId == null) return;
    const aliasValue = editingAliasValue.trim();
    if (!aliasValue) { toast.error("Alias name cannot be empty"); return; }
    if (editingTargets.length === 0) { toast.error("Add at least one target tag"); return; }
    updateAliasMutation.mutate({
      id: editingAliasId,
      alias: aliasValue,
      targets: editingTargets,
      description: editingAliasDescription.trim() || null,
    });
  };

  const startEdit = (id: number, name: string) => {
    setEditingId(id);
    setEditingValue(name);
    setEditingDescId(null);
  };

  const commitEdit = (id: number) => {
    const trimmed = editingValue.trim();
    if (!trimmed) return;
    renameMutation.mutate({ id, newName: trimmed });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditingValue("");
  };

  const startDescEdit = (id: number, description: string | null) => {
    setEditingDescId(id);
    setEditingDescValue(description ?? "");
    setEditingId(null);
    setTimeout(() => descTextareaRef.current?.focus(), 50);
  };

  const commitDescEdit = (id: number) => {
    const trimmed = editingDescValue.trim();
    descMutation.mutate({ id, description: trimmed || null });
  };

  const cancelDescEdit = () => {
    setEditingDescId(null);
    setEditingDescValue("");
  };

  const filtered = tags.filter((t) =>
    t.name.toLowerCase().includes(search.toLowerCase()) ||
    (t.description ?? "").toLowerCase().includes(search.toLowerCase())
  );

  const libraryDescription = library === "activity"
    ? "All activity tags used across footage sessions. Edit names, add descriptions, or remove tags — changes apply to future autocomplete suggestions only and do not affect already-saved footage files."
    : "Topic tags group footage by subject matter. Edit names, add descriptions, or remove tags — changes apply to future autocomplete suggestions only and do not affect already-saved footage files.";

  const emptyLibraryHint = library === "activity"
    ? "No activity tags yet. Tags are saved automatically when you finalize footage."
    : "No topic tags yet. Add one above to get started.";

  // Topic Tags use the Minnesota purple accent on header icons + the primary
  // action buttons to keep them visually distinct from the Activity Tag flow
  // (which uses the app's theme primary green).
  const iconStyle = library === "topic" ? { color: MINNESOTA_PURPLE } : undefined;
  const iconClass = library === "topic" ? "w-5 h-5" : "w-5 h-5 text-primary";
  const addBtnStyle =
    library === "topic" ? { backgroundColor: MINNESOTA_PURPLE, color: "white" } : undefined;

  return (
    <>
      {/* ── Tag Library card ───────────────────────────────────────────── */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <Tag className={iconClass} style={iconStyle} />
            <CardTitle className="text-base">{labels.library}</CardTitle>
          </div>
          <CardDescription>{libraryDescription}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Add new tag (name + optional description together) */}
          <div className="space-y-2">
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Plus className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  value={newTagValue}
                  onChange={(e) => setNewTagValue(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleAddTag(); } }}
                  placeholder="Add a new tag..."
                  className="pl-9 h-9 text-sm"
                  disabled={createMutation.isPending}
                />
              </div>
              <Button
                size="sm"
                onClick={handleAddTag}
                disabled={!newTagValue.trim() || createMutation.isPending}
                className="h-9 px-4 shrink-0"
                style={addBtnStyle}
              >
                {createMutation.isPending ? "Adding…" : "Add Tag"}
              </Button>
            </div>
            <div className="relative">
              <textarea
                value={newTagDescription}
                onChange={(e) => setNewTagDescription(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); handleAddTag(); } }}
                placeholder="Optional description (added with the tag)…"
                maxLength={500}
                rows={2}
                disabled={createMutation.isPending}
                className="w-full text-sm px-3 py-2 rounded-md border border-input bg-background resize-none focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-50"
              />
              {newTagDescription.length > 0 && (
                <span className="absolute bottom-2 right-2 text-[10px] text-muted-foreground pointer-events-none">{newTagDescription.length}/500</span>
              )}
            </div>
          </div>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search tags or descriptions..."
              className="pl-9 h-9 text-sm"
            />
          </div>

          {/* Tag count */}
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>
              {filtered.length} {filtered.length === 1 ? "tag" : "tags"}
              {search && ` matching "${search}"`}
            </span>
            {tags.length > 0 && <span>{tags.length} total</span>}
          </div>

          {/* Tag list */}
          {isLoading ? (
            <div className="py-8 text-center text-sm text-muted-foreground">Loading tags…</div>
          ) : isError ? (
            <div className="py-8 text-center">
              <div className="w-10 h-10 rounded-full bg-destructive/10 flex items-center justify-center mx-auto mb-3">
                <X className="w-5 h-5 text-destructive" />
              </div>
              <p className="text-sm font-medium text-foreground mb-1">Could not load tags</p>
              <p className="text-xs text-muted-foreground mb-4">
                {error?.message?.includes('fetch') || error?.message?.includes('network') || error?.message?.includes('Failed')
                  ? 'Network error — check your connection and try again.'
                  : (error?.message ?? 'Connection issue — the request did not complete.')}
              </p>
              <Button size="sm" variant="outline" onClick={() => refetch()} disabled={isFetching} className="gap-2">
                {isFetching ? 'Retrying…' : 'Try Again'}
              </Button>
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-8 text-center">
              <Tag className="w-8 h-8 text-muted-foreground/40 mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">
                {search ? "No tags match your search." : emptyLibraryHint}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-border rounded-lg border border-border overflow-hidden">
              {filtered.map((tag) => (
                <div key={tag.id} className="px-3 py-2.5 bg-card hover:bg-muted/30 transition-colors group">
                  {/* Name row */}
                  <div className="flex items-center gap-3">
                    {editingId === tag.id ? (
                      <>
                        <Input
                          autoFocus
                          value={editingValue}
                          onChange={(e) => setEditingValue(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") { e.preventDefault(); commitEdit(tag.id); }
                            if (e.key === "Escape") cancelEdit();
                          }}
                          className="h-7 text-xs flex-1"
                        />
                        <button onClick={() => commitEdit(tag.id)} disabled={renameMutation.isPending} className="text-primary hover:text-primary/80 p-1 rounded" title="Save">
                          <Check className="w-3.5 h-3.5" />
                        </button>
                        <button onClick={cancelEdit} className="text-muted-foreground hover:text-foreground p-1 rounded" title="Cancel">
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </>
                    ) : (
                      <>
                        <span className="flex-1 text-sm font-medium text-foreground">{tag.name}</span>
                        <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-5 shrink-0">
                          {tag.usageCount}x
                        </Badge>
                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button onClick={() => startEdit(tag.id, tag.name)} className="text-muted-foreground hover:text-foreground p-1 rounded" title="Rename tag">
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={() => startDescEdit(tag.id, tag.description ?? null)}
                            className="text-muted-foreground hover:text-foreground p-1 rounded"
                            title={tag.description ? "Edit description" : "Add description"}
                          >
                            <FileText className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={() => deleteMutation.mutate({ id: tag.id })}
                            disabled={deleteMutation.isPending}
                            className="text-muted-foreground hover:text-destructive p-1 rounded"
                            title="Delete tag"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </>
                    )}
                  </div>

                  {/* Description row */}
                  {editingDescId === tag.id ? (
                    <div className="mt-2 space-y-1.5">
                      <textarea
                        ref={descTextareaRef}
                        value={editingDescValue}
                        onChange={(e) => setEditingDescValue(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Escape") cancelDescEdit();
                          if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); commitDescEdit(tag.id); }
                        }}
                        placeholder="Optional short description (e.g. 'Supplemental oxygen therapy')"
                        maxLength={500}
                        rows={2}
                        className="w-full text-xs px-2.5 py-1.5 rounded-md border border-input bg-background resize-none focus:outline-none focus:ring-1 focus:ring-primary"
                      />
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => commitDescEdit(tag.id)}
                          disabled={descMutation.isPending}
                          className="flex items-center gap-1 text-xs text-primary hover:text-primary/80 font-medium"
                        >
                          <Check className="w-3 h-3" />
                          {descMutation.isPending ? "Saving…" : "Save"}
                        </button>
                        <button onClick={cancelDescEdit} className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
                          <X className="w-3 h-3" />
                          Cancel
                        </button>
                        <span className="ml-auto text-[10px] text-muted-foreground">{editingDescValue.length}/500</span>
                      </div>
                    </div>
                  ) : tag.description ? (
                    <p
                      className="mt-1 text-xs text-muted-foreground cursor-pointer hover:text-foreground transition-colors"
                      onClick={() => startDescEdit(tag.id, tag.description ?? null)}
                      title="Click to edit description"
                    >
                      {tag.description}
                    </p>
                  ) : (
                    <p
                      className="mt-0.5 text-[11px] text-muted-foreground/50 italic opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                      onClick={() => startDescEdit(tag.id, null)}
                    >
                      + Add description
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Tag Aliases card ───────────────────────────────────────────── */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <Link2 className={iconClass} style={iconStyle} />
            <CardTitle className="text-base">{labels.tag} Aliases</CardTitle>
          </div>
          <CardDescription>
            Map shorthand to one or more canonical tag names. When you type an alias on the upload screen, it expands inline — a single alias can pull in multiple tags at once.
            <br />
            <span className="text-xs text-muted-foreground mt-1 block">
              Example: <span className="font-mono bg-muted px-1 rounded">exam</span> <ArrowRight className="inline w-3 h-3" /> <span className="font-mono bg-muted px-1 rounded">ATNR Check</span> + <span className="font-mono bg-muted px-1 rounded">STNR Check</span> + <span className="font-mono bg-muted px-1 rounded">Babinski</span>
            </span>
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Add alias form */}
          <div className="space-y-2.5 rounded-lg border border-border bg-muted/20 p-3">
            <div className="flex gap-2 items-start">
              <div className="w-32 space-y-1 shrink-0">
                <label className="text-xs font-medium text-muted-foreground">Alias (shorthand)</label>
                <Input
                  value={aliasInput}
                  onChange={(e) => setAliasInput(e.target.value)}
                  placeholder="e.g. exam"
                  className="h-9 text-sm font-mono"
                  disabled={createAliasMutation.isPending}
                />
              </div>
              <ArrowRight className="w-4 h-4 text-muted-foreground mt-8 shrink-0" />
              <div className="flex-1 space-y-1">
                <label className="text-xs font-medium text-muted-foreground">
                  Targets {addTargets.length > 0 && <span className="text-muted-foreground/70">({addTargets.length})</span>}
                </label>
                <TagChipEditor
                  targets={addTargets}
                  onChange={setAddTargets}
                  api={api}
                  disabled={createAliasMutation.isPending}
                  onEnterEmpty={handleAddAlias}
                />
              </div>
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground">
                Description <span className="text-muted-foreground/70">(optional)</span>
              </label>
              <textarea
                value={addDescription}
                onChange={(e) => setAddDescription(e.target.value)}
                placeholder="What does this alias mean? (e.g. 'Full primitive reflex screen')"
                maxLength={500}
                rows={2}
                disabled={createAliasMutation.isPending}
                className="w-full text-xs px-2.5 py-1.5 rounded-md border border-input bg-background resize-none focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-50"
              />
            </div>
            <div className="flex justify-end">
              <Button
                size="sm"
                onClick={handleAddAlias}
                disabled={!aliasInput.trim() || addTargets.length === 0 || createAliasMutation.isPending}
                className="h-9 px-4"
                style={addBtnStyle}
              >
                {createAliasMutation.isPending ? "Adding…" : "Add Alias"}
              </Button>
            </div>
          </div>

          {/* Alias list */}
          {aliasesLoading ? (
            <div className="py-6 text-center text-sm text-muted-foreground">Loading aliases…</div>
          ) : aliases.length === 0 ? (
            <div className="py-6 text-center">
              <Link2 className="w-7 h-7 text-muted-foreground/30 mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">No aliases yet. Add one above to get started.</p>
            </div>
          ) : (
            <div className="divide-y divide-border rounded-lg border border-border overflow-hidden">
              {aliases.map((alias) => {
                const aliasTargets = (alias.targets && alias.targets.length > 0)
                  ? alias.targets
                  : [alias.canonicalName];
                const isEditing = editingAliasId === alias.id;

                if (isEditing) {
                  return (
                    <div key={alias.id} className="px-3 py-3 bg-muted/30 space-y-2.5">
                      <div className="flex gap-2 items-start">
                        <div className="w-32 space-y-1 shrink-0">
                          <label className="text-[11px] font-medium text-muted-foreground">Alias</label>
                          <Input
                            value={editingAliasValue}
                            onChange={(e) => setEditingAliasValue(e.target.value)}
                            className="h-8 text-sm font-mono"
                            disabled={updateAliasMutation.isPending}
                          />
                        </div>
                        <ArrowRight className="w-4 h-4 text-muted-foreground mt-7 shrink-0" />
                        <div className="flex-1 space-y-1">
                          <label className="text-[11px] font-medium text-muted-foreground">
                            Targets ({editingTargets.length})
                          </label>
                          <TagChipEditor
                            targets={editingTargets}
                            onChange={setEditingTargets}
                            api={api}
                            disabled={updateAliasMutation.isPending}
                            onEnterEmpty={commitEditAlias}
                            compact
                          />
                        </div>
                      </div>
                      <div className="space-y-1">
                        <label className="text-[11px] font-medium text-muted-foreground">
                          Description <span className="text-muted-foreground/70">(optional)</span>
                        </label>
                        <textarea
                          value={editingAliasDescription}
                          onChange={(e) => setEditingAliasDescription(e.target.value)}
                          placeholder="Optional notes about this alias"
                          maxLength={500}
                          rows={2}
                          disabled={updateAliasMutation.isPending}
                          className="w-full text-xs px-2.5 py-1.5 rounded-md border border-input bg-background resize-none focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-50"
                        />
                      </div>
                      <div className="flex justify-end gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={cancelEditAlias}
                          disabled={updateAliasMutation.isPending}
                          className="h-8 px-3 text-xs"
                        >
                          Cancel
                        </Button>
                        <Button
                          size="sm"
                          onClick={commitEditAlias}
                          disabled={!editingAliasValue.trim() || editingTargets.length === 0 || updateAliasMutation.isPending}
                          className="h-8 px-3 text-xs"
                          style={addBtnStyle}
                        >
                          {updateAliasMutation.isPending ? "Saving…" : "Save"}
                        </Button>
                      </div>
                    </div>
                  );
                }

                return (
                  <div key={alias.id} className="px-3 py-2.5 bg-card hover:bg-muted/30 transition-colors group">
                    <div className="flex items-center gap-3">
                      <span className="font-mono text-sm font-semibold text-foreground min-w-[80px]">{alias.alias}</span>
                      <ArrowRight className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                      <span className="flex-1 text-sm text-foreground flex flex-wrap items-center gap-x-1 gap-y-0.5">
                        {aliasTargets.map((t, i) => (
                          <span key={`${t}-${i}`} className="inline-flex items-center">
                            <span>{t}</span>
                            {i < aliasTargets.length - 1 && <span className="text-muted-foreground mx-1">+</span>}
                          </span>
                        ))}
                      </span>
                      <button
                        onClick={() => startEditAlias(alias)}
                        disabled={updateAliasMutation.isPending || deleteAliasMutation.isPending}
                        className="text-muted-foreground hover:text-foreground p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                        title="Edit alias"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => deleteAliasMutation.mutate({ id: alias.id })}
                        disabled={deleteAliasMutation.isPending}
                        className="text-muted-foreground hover:text-destructive p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                        title="Delete alias"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    {alias.description && (
                      <p className="mt-1 ml-[92px] text-xs text-muted-foreground italic">
                        {alias.description}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Import & Export card ───────────────────────────────────────── */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <FileSpreadsheet className={iconClass} style={iconStyle} />
            <CardTitle className="text-base">Import & Export Library</CardTitle>
          </div>
          <CardDescription>
            Bulk-import tags from a CSV, or download a snapshot of the current library. Exported files include the tag name, its description, and any aliases — usage counts are omitted.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <Button
              variant="outline"
              onClick={() => setCsvImportOpen(true)}
              className="h-10 gap-2"
              title="Import a batch of tags from a CSV file"
            >
              <Upload className="w-4 h-4" />
              Import CSV
            </Button>
            <Button
              variant="outline"
              onClick={handleExportCsv}
              disabled={tags.length === 0}
              className="h-10 gap-2"
              title="Download the current library as a CSV spreadsheet"
            >
              <Download className="w-4 h-4" />
              Export CSV
            </Button>
          </div>
        </CardContent>
      </Card>

      <TagCsvImportDialog
        open={csvImportOpen}
        onOpenChange={setCsvImportOpen}
        library={library}
        onImported={() => {
          utilsApi.list.invalidate();
          utilsApi.listAliases.invalidate();
        }}
      />
    </>
  );
}

// ─── Providers section ────────────────────────────────────────────────────────
// A third Tag-Library mode. Pick a Location, then manage that location's roster
// of providers (name + photo). Styled entirely in Iowa blue to mirror how the
// Activity flow is green and the Topic flow is Minnesota purple.

const PROVIDER_LOCATIONS = ["Iowa", "Minnesota", "Colorado"] as const;
type ProviderLocation = (typeof PROVIDER_LOCATIONS)[number];

// Each Location keeps its usual brand colour from the rest of the app (see the
// Location chips in PatientStep / Footage Search): Iowa blue, Minnesota purple,
// Colorado green. Only the "Providers" tab + the Provider Library controls use
// Iowa blue as the workflow accent.
const LOCATION_COLORS: Record<ProviderLocation, string> = {
  Iowa: IOWA_BLUE,
  Minnesota: MINNESOTA_PURPLE,
  Colorado: "#1b6e4a",
};

// Employment status. Only Active providers are offered in the upload
// provider picker and shown on Weekly Clips; Hiatus (seasonal / temporarily
// away) and No Longer Employed keep every tag they already have on clips.
type ProviderStatus = "active" | "hiatus" | "former";
const PROVIDER_STATUS_ORDER: ProviderStatus[] = ["active", "hiatus", "former"];
const PROVIDER_STATUS_META: Record<ProviderStatus, { label: string; className: string }> = {
  active: { label: "Active", className: "bg-emerald-50 text-emerald-800 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-900" },
  hiatus: { label: "On Hiatus", className: "bg-amber-50 text-amber-800 border-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-900" },
  former: { label: "No Longer Employed", className: "bg-muted text-muted-foreground border-border" },
};

// Extensions we treat as images even when the browser reports an empty MIME
// type (common for HEIC and some drag-drop sources).
const IMAGE_EXT_RE = /\.(jpe?g|png|gif|webp|bmp|tiff?|heic|heif|avif)$/i;

function looksLikeImage(file: File): boolean {
  return file.type.startsWith("image/") || IMAGE_EXT_RE.test(file.name);
}

function isHeic(file: File): boolean {
  return /image\/hei[cf]/i.test(file.type) || /\.hei[cf]$/i.test(file.name);
}

/**
 * Turn a chosen File into an object-URL the cropper can load. HEIC/HEIF can't
 * be decoded by <canvas>/<img> in most browsers, so those are converted to a
 * JPEG blob first (via the lazily-loaded `heic-to`, so its libheif wasm only
 * ships when actually needed). Caller owns the returned URL and must revoke it.
 */
async function fileToCropSource(file: File): Promise<string> {
  if (isHeic(file)) {
    const { heicTo } = await import("heic-to");
    const jpeg = await heicTo({ blob: file, type: "image/jpeg", quality: 0.92 });
    return URL.createObjectURL(jpeg);
  }
  return URL.createObjectURL(file);
}

function ProviderAvatar({
  photoUrl,
  name,
  size = 40,
}: {
  photoUrl?: string | null;
  name: string;
  size?: number;
}) {
  return (
    <span
      className="inline-flex items-center justify-center rounded-full overflow-hidden bg-muted border border-border shrink-0"
      style={{ width: size, height: size }}
    >
      {photoUrl ? (
        <img src={photoUrl} alt={name} className="w-full h-full object-cover" />
      ) : (
        <User className="text-muted-foreground" style={{ width: size * 0.5, height: size * 0.5 }} />
      )}
    </span>
  );
}

/**
 * Circular photo picker with drag-and-drop. Accepts any image format (incl.
 * HEIC/HEIF), then opens a zoom-and-crop dialog; the cropped square JPEG is
 * handed back via `onPick`. `accent` themes the crop dialog to the location.
 */
function ProviderPhotoPicker({
  photoUrl,
  name,
  disabled,
  accent,
  onPick,
  onClear,
  size = 56,
  showHint = false,
}: {
  photoUrl: string | null;
  name: string;
  disabled?: boolean;
  accent: string;
  onPick: (dataUrl: string) => void;
  onClear: () => void;
  size?: number;
  showHint?: boolean;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [cropSrc, setCropSrc] = useState<string | null>(null);
  const [cropOpen, setCropOpen] = useState(false);

  const processFile = async (file: File | undefined) => {
    if (!file) return;
    if (!looksLikeImage(file)) {
      toast.error("Please choose an image file");
      return;
    }
    setBusy(true);
    try {
      const src = await fileToCropSource(file);
      setCropSrc(src);
      setCropOpen(true);
    } catch {
      toast.error("Could not read that image");
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const closeCrop = (open: boolean) => {
    setCropOpen(open);
    if (!open && cropSrc) {
      URL.revokeObjectURL(cropSrc);
      setCropSrc(null);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    if (disabled || busy) return;
    processFile(e.dataTransfer.files?.[0]);
  };

  return (
    <div className="flex flex-col items-center gap-1 shrink-0">
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={disabled || busy}
        onDragOver={(e) => { e.preventDefault(); if (!dragging) setDragging(true); }}
        onDragEnter={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
        className={cn(
          "relative rounded-full overflow-hidden border border-dashed bg-muted flex items-center justify-center transition-colors disabled:opacity-50",
          dragging ? "border-solid" : "border-border hover:border-foreground/30",
        )}
        style={{ width: size, height: size, ...(dragging ? { borderColor: accent, boxShadow: `0 0 0 2px ${accent}` } : {}) }}
        title={photoUrl ? "Change photo — click or drop an image" : "Add a photo — click or drop an image"}
      >
        {photoUrl ? (
          <img src={photoUrl} alt={name || "Provider photo"} className="w-full h-full object-cover" />
        ) : (
          <ImagePlus className="w-5 h-5 text-muted-foreground" />
        )}
        {busy && (
          <span className="absolute inset-0 flex items-center justify-center bg-background/70 text-[10px] text-muted-foreground">
            …
          </span>
        )}
      </button>
      {photoUrl && !disabled ? (
        <button
          type="button"
          onClick={onClear}
          className="text-[10px] text-muted-foreground hover:text-destructive"
        >
          Remove
        </button>
      ) : showHint ? (
        <span className="text-[10px] text-muted-foreground text-center leading-tight">Drag&nbsp;or click</span>
      ) : null}
      <input
        ref={inputRef}
        type="file"
        accept="image/*,.heic,.heif,.avif"
        className="hidden"
        onChange={(e) => processFile(e.target.files?.[0])}
      />
      <ImageCropDialog
        open={cropOpen}
        onOpenChange={closeCrop}
        imageSrc={cropSrc}
        accent={accent}
        onSave={onPick}
      />
    </div>
  );
}

function ProvidersSection() {
  const [location, setLocation] = useState<ProviderLocation | null>(null);
  useApplyDefaultLocation(location, setLocation);

  return (
    <>
      {/* ── Location selector ─────────────────────────────────────────── */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <MapPin className="w-5 h-5" style={{ color: IOWA_BLUE }} />
            <CardTitle className="text-base">Location</CardTitle>
          </div>
          <CardDescription>
            Pick a Location to view and manage its Provider Library.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-3">
            {PROVIDER_LOCATIONS.map((loc) => {
              const isSelected = location === loc;
              const accent = LOCATION_COLORS[loc];
              return (
                <button
                  key={loc}
                  type="button"
                  onClick={() => setLocation(loc)}
                  className={cn(
                    "flex items-center justify-center gap-2 px-4 py-3 rounded-lg border text-sm font-semibold transition-all",
                    "focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
                    isSelected
                      ? "border-transparent text-white shadow-sm"
                      : "border-border bg-background hover:bg-muted/60 hover:border-foreground/20 text-foreground",
                  )}
                  style={
                    isSelected
                      ? {
                          backgroundColor: accent,
                          boxShadow: `0 0 0 2px var(--background), 0 0 0 4px ${accent}`,
                        }
                      : undefined
                  }
                >
                  {isSelected && <Check className="w-4 h-4" />}
                  {loc}
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* ── Provider Library for the selected location ─────────────────── */}
      {location && <ProviderLibrary key={location} location={location} />}
    </>
  );
}

function ProviderLibrary({ location }: { location: ProviderLocation }) {
  const [newName, setNewName] = useState("");
  const [newPhoto, setNewPhoto] = useState<string | null>(null);

  const [editingId, setEditingId] = useState<number | null>(null);
  const [editName, setEditName] = useState("");
  const [editPhoto, setEditPhoto] = useState<string | null>(null);
  const [editLocation, setEditLocation] = useState<ProviderLocation>(location);

  const utils = trpc.useUtils();
  const invalidate = () => utils.providers.list.invalidate({ location });

  const { data: providers = [], isLoading, isError, refetch, isFetching } =
    trpc.providers.list.useQuery({ location }, { retry: 2, retryDelay: 1000 });

  const createMutation = trpc.providers.create.useMutation({
    onSuccess: () => {
      invalidate();
      setNewName("");
      setNewPhoto(null);
      toast.success("Provider added");
    },
    onError: (err) => toast.error(err.message ?? "Failed to add provider"),
  });

  const updateMutation = trpc.providers.update.useMutation({
    onSuccess: (_data, variables) => {
      // A location change moves the provider to another roster, so refresh
      // every location's list (not just this one).
      utils.providers.list.invalidate();
      setEditingId(null);
      const moved = variables.location != null && variables.location !== location;
      toast.success(moved ? `Moved to ${variables.location}` : "Provider updated");
    },
    onError: (err) => toast.error(err.message ?? "Failed to update provider"),
  });

  const statusMutation = trpc.providers.update.useMutation({
    onSuccess: (_data, variables) => {
      invalidate();
      const name = providers.find((p) => p.id === variables.id)?.name ?? "Provider";
      const label = PROVIDER_STATUS_META[variables.status as ProviderStatus]?.label ?? "updated";
      toast.success(
        variables.status === "active"
          ? `${name} is Active — available for tagging and on Weekly Clips`
          : `${name} set to ${label} — hidden from tagging and Weekly Clips; past tags kept`,
      );
    },
    onError: (err) => toast.error(err.message ?? "Failed to update status"),
  });

  const deleteMutation = trpc.providers.delete.useMutation({
    onMutate: async ({ id }) => {
      await utils.providers.list.cancel({ location });
      const prev = utils.providers.list.getData({ location });
      utils.providers.list.setData({ location }, (old) => old?.filter((p) => p.id !== id) ?? []);
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) utils.providers.list.setData({ location }, ctx.prev);
      toast.error("Failed to delete provider");
    },
    onSettled: () => invalidate(),
  });

  const handleAdd = () => {
    const trimmed = newName.trim();
    if (!trimmed) {
      toast.error("Provider name is required");
      return;
    }
    createMutation.mutate({ location, name: trimmed, photoUrl: newPhoto ?? undefined });
  };

  const startEdit = (p: { id: number; name: string; photoUrl: string | null }) => {
    setEditingId(p.id);
    setEditName(p.name);
    setEditPhoto(p.photoUrl);
    setEditLocation(location);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditName("");
    setEditPhoto(null);
    setEditLocation(location);
  };

  const commitEdit = (id: number) => {
    const trimmed = editName.trim();
    if (!trimmed) {
      toast.error("Provider name cannot be empty");
      return;
    }
    updateMutation.mutate({ id, name: trimmed, photoUrl: editPhoto, location: editLocation });
  };

  // The Provider Library inherits the selected location's brand colour, so the
  // whole workflow reads as "Iowa" / "Minnesota" / "Colorado" end-to-end.
  const accent = LOCATION_COLORS[location];
  const accentBtnStyle = { backgroundColor: accent, color: "white" };

  // Active first, then On Hiatus, then No Longer Employed (each by name, as
  // the server returns them), with a divider before the first non-active one.
  const statusRank = (p: { status?: string | null }) =>
    Math.max(0, PROVIDER_STATUS_ORDER.indexOf((p.status ?? "active") as ProviderStatus));
  const sortedProviders = [...providers].sort((a, b) => statusRank(a) - statusRank(b));
  const firstInactiveIdx = sortedProviders.findIndex((p) => statusRank(p) > 0);

  const renderProviderRow = (p: (typeof providers)[number]) => {
    const isEditing = editingId === p.id;
    if (isEditing) {
      return (
        <div key={p.id} className="px-3 py-3 bg-muted/30">
          <div className="flex items-end gap-3">
            <ProviderPhotoPicker
              photoUrl={editPhoto}
              name={editName}
              disabled={updateMutation.isPending}
              accent={accent}
              onPick={setEditPhoto}
              onClear={() => setEditPhoto(null)}
            />
            <div className="flex-1 space-y-1">
              <label className="text-xs font-medium text-muted-foreground">Name</label>
              <Input
                autoFocus
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    commitEdit(p.id);
                  }
                  if (e.key === "Escape") cancelEdit();
                }}
                className="h-9 text-sm"
                disabled={updateMutation.isPending}
              />
            </div>
            <div className="flex items-center gap-1.5 shrink-0">
              <Button
                size="sm"
                onClick={() => commitEdit(p.id)}
                disabled={!editName.trim() || updateMutation.isPending}
                className="h-9 px-3 text-xs gap-1"
                style={accentBtnStyle}
              >
                <Check className="w-3.5 h-3.5" />
                {updateMutation.isPending ? "Saving…" : "Save"}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={cancelEdit}
                disabled={updateMutation.isPending}
                className="h-9 px-3 text-xs"
              >
                Cancel
              </Button>
            </div>
          </div>

          {/* Location — pick a different one to move the provider to another roster */}
          <div className="mt-3 space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground">Location</label>
            <div className="flex flex-wrap gap-1.5">
              {PROVIDER_LOCATIONS.map((loc) => {
                const isSelected = editLocation === loc;
                const locColor = LOCATION_COLORS[loc];
                return (
                  <button
                    key={loc}
                    type="button"
                    onClick={() => setEditLocation(loc)}
                    disabled={updateMutation.isPending}
                    className={cn(
                      "flex items-center gap-1 px-2.5 py-1 rounded-full border text-xs font-semibold transition-all",
                      "focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1",
                      isSelected
                        ? "border-transparent text-white"
                        : "border-border bg-background hover:bg-muted/50 text-foreground",
                    )}
                    style={isSelected ? { backgroundColor: locColor } : undefined}
                  >
                    {isSelected && <Check className="w-3 h-3" />}
                    {loc}
                  </button>
                );
              })}
            </div>
            {editLocation !== location && (
              <p className="text-[11px] text-muted-foreground">
                Saving will move this provider from {location} to {editLocation}.
              </p>
            )}
          </div>
        </div>
      );
    }

    return (
      <div key={p.id} className="px-3 py-2.5 bg-card hover:bg-muted/30 transition-colors group">
        <div className="flex items-center gap-3">
          <span className={cn("inline-flex shrink-0", p.status !== "active" && "opacity-60")}>
            <ProviderAvatar photoUrl={p.photoUrl} name={p.name} />
          </span>
          <span
            className={cn(
              "flex-1 text-sm font-medium",
              p.status === "active" ? "text-foreground" : "text-muted-foreground",
            )}
          >
            {p.name}
          </span>
          <Select
            value={p.status ?? "active"}
            onValueChange={(v) => {
              if (v !== p.status) statusMutation.mutate({ id: p.id, status: v as ProviderStatus });
            }}
            disabled={statusMutation.isPending}
          >
            <SelectTrigger
              className={cn(
                "h-7 w-auto gap-1.5 rounded-full border px-2.5 text-xs font-medium",
                PROVIDER_STATUS_META[(p.status ?? "active") as ProviderStatus].className,
              )}
              title="Status — only Active providers can be tagged and appear on Weekly Clips"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PROVIDER_STATUS_ORDER.map((st) => (
                <SelectItem key={st} value={st} className="text-xs">
                  {PROVIDER_STATUS_META[st].label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <button
              onClick={() => startEdit(p)}
              className="text-muted-foreground hover:text-foreground p-1 rounded"
              title="Edit provider"
            >
              <Pencil className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={() => deleteMutation.mutate({ id: p.id })}
              disabled={deleteMutation.isPending}
              className="text-muted-foreground hover:text-destructive p-1 rounded"
              title="Delete provider"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>
    );
  };


  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <Users className="w-5 h-5" style={{ color: accent }} />
          <CardTitle className="text-base">{location} Provider Library</CardTitle>
        </div>
        <CardDescription>
          The clinical providers associated with {location}. Add a provider with a name and an
          optional photo, or edit an existing entry. Set a provider to On Hiatus or No Longer
          Employed to stop them being tagged and hide them from Weekly Clips — clips already
          tagged with them keep the tag.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Add provider */}
        <div className="flex items-end gap-3 rounded-lg border border-border bg-muted/20 p-3">
          <ProviderPhotoPicker
            photoUrl={newPhoto}
            name={newName}
            disabled={createMutation.isPending}
            accent={accent}
            onPick={setNewPhoto}
            onClear={() => setNewPhoto(null)}
            showHint
          />
          <div className="flex-1 space-y-1">
            <label className="text-xs font-medium text-muted-foreground">Name</label>
            <Input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleAdd();
                }
              }}
              placeholder="Provider name…"
              className="h-9 text-sm"
              disabled={createMutation.isPending}
            />
          </div>
          <Button
            size="sm"
            onClick={handleAdd}
            disabled={!newName.trim() || createMutation.isPending}
            className="h-9 px-4 shrink-0 gap-1.5"
            style={accentBtnStyle}
          >
            <Plus className="w-4 h-4" />
            {createMutation.isPending ? "Adding…" : "Add Provider"}
          </Button>
        </div>

        {/* Provider list */}
        {isLoading ? (
          <div className="py-8 text-center text-sm text-muted-foreground">Loading providers…</div>
        ) : isError ? (
          <div className="py-8 text-center">
            <div className="w-10 h-10 rounded-full bg-destructive/10 flex items-center justify-center mx-auto mb-3">
              <X className="w-5 h-5 text-destructive" />
            </div>
            <p className="text-sm font-medium text-foreground mb-1">Could not load providers</p>
            <Button size="sm" variant="outline" onClick={() => refetch()} disabled={isFetching} className="gap-2">
              {isFetching ? "Retrying…" : "Try Again"}
            </Button>
          </div>
        ) : providers.length === 0 ? (
          <div className="py-8 text-center">
            <Users className="w-8 h-8 text-muted-foreground/40 mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">
              No providers for {location} yet. Add one above to get started.
            </p>
          </div>
        ) : (
          <div className="divide-y divide-border rounded-lg border border-border overflow-hidden">
            {sortedProviders.map((p, idx) => (
              <Fragment key={p.id}>
                {idx === firstInactiveIdx && (
                  <div className="px-3 py-2 bg-muted/50 text-[11px] text-muted-foreground">
                    <span className="font-semibold uppercase tracking-wide">Not active</span> — hidden from the
                    upload provider picker and Weekly Clips. Clips already tagged with them keep the tag, and
                    reports still count them.
                  </div>
                )}
                {renderProviderRow(p)}
              </Fragment>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
